import type { VacancyIntakeSnapshot } from './vacancyIntakeVersions';

const stripTags = (html: string) =>
  String(html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();

function truncate(s: string, max: number): string {
  const t = s.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, Math.max(0, max - 1))}…`;
}

/**
 * Dutch summary lines for plain-text (HTML stripped) changes using prefix/suffix trim on words.
 */
export function summarizePlainTextChange(
  label: string,
  prevHtmlOrText: string,
  nextHtmlOrText: string,
  maxSnippet = 200
): string[] {
  const a = stripTags(prevHtmlOrText);
  const b = stripTags(nextHtmlOrText);
  if (a === b) return [];

  const wa = a.split(/\s+/).filter(Boolean);
  const wb = b.split(/\s+/).filter(Boolean);
  let i = 0;
  while (i < wa.length && i < wb.length && wa[i] === wb[i]) i++;
  let j = 0;
  while (
    j < wa.length - i &&
    j < wb.length - i &&
    wa[wa.length - 1 - j] === wb[wb.length - 1 - j]
  ) {
    j++;
  }
  const rem = wa.slice(i, wa.length - j).join(' ');
  const add = wb.slice(i, wb.length - j).join(' ');
  const out: string[] = [];
  if (rem) out.push(`${label} — verwijderd: ${truncate(rem, maxSnippet)}`);
  if (add) out.push(`${label} — toegevoegd: ${truncate(add, maxSnippet)}`);
  if (out.length === 0) out.push(`${label} — aangepast`);
  return out;
}

export function intakeSnapshotTextDiffSummaries(
  prev: VacancyIntakeSnapshot,
  next: VacancyIntakeSnapshot,
  maxTotal = 36
): string[] {
  const lines: string[] = [];
  const push = (xs: string[]) => {
    for (const x of xs) {
      if (lines.length >= maxTotal) return;
      lines.push(x);
    }
  };

  if ((prev.title || '') !== (next.title || '')) {
    if (prev.title) push([`Functietitel — was: ${truncate(prev.title, 140)}`]);
    if (next.title) push([`Functietitel — nu: ${truncate(next.title, 140)}`]);
  }

  const pd = prev.data || {};
  const nd = next.data || {};

  push(summarizePlainTextChange('Vacaturetekst', String(pd.vacancyText ?? ''), String(nd.vacancyText ?? '')));
  push(summarizePlainTextChange('Organisatietekst', String(pd.organizationText ?? ''), String(nd.organizationText ?? '')));
  push(summarizePlainTextChange('Functie-eisen', String(pd.suggestions ?? ''), String(nd.suggestions ?? '')));
  push(summarizePlainTextChange('Arbeidsvoorwaarden', String(pd.benefits ?? ''), String(nd.benefits ?? '')));

  if (stripTags(prev.description || '') !== stripTags(next.description || '')) {
    push(
      summarizePlainTextChange(
        'Volledige beschrijving (incl. organisatieblok)',
        prev.description || '',
        next.description || ''
      )
    );
  }

  if (!eqJson(pd.salary, nd.salary)) {
    push([`Salaris — was: ${truncate(JSON.stringify(pd.salary ?? null), 160)}`]);
    push([`Salaris — nu: ${truncate(JSON.stringify(nd.salary ?? null), 160)}`]);
  }

  return lines;
}

function eqJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/** Prefer detailed text lines; drop redundant high-level describe lines. */
export function mergeStructuralAndTextDiffLines(textFirst: string[], structural: string[], max = 40): string[] {
  const out = [...textFirst];
  const has = (sub: string) => textFirst.some((l) => l.includes(sub));
  for (const s of structural) {
    if (s === 'Functietitel aangepast' && has('Functietitel')) continue;
    if (s === 'Vacaturetekst / beschrijving (HTML) aangepast' && (has('Vacaturetekst') || has('beschrijving'))) continue;
    if (s === 'Organisatietekst aangepast' && has('Organisatie')) continue;
    if (s === 'Functie-eisen / vereisten aangepast' && has('Functie-eisen')) continue;
    if (s === 'Arbeidsvoorwaarden aangepast' && has('Arbeidsvoorwaarden')) continue;
    if (s === 'Brontekst intake aangepast' && has('Vacaturetekst')) continue;
    if (s === 'Definitieve vacaturetekst aangepast' && (has('Vacaturetekst') || has('Volledige beschrijving'))) continue;
    if (s === 'Salaris aangepast' && has('Salaris')) continue;
    out.push(s);
    if (out.length >= max) break;
  }
  return out.slice(0, max);
}
